import { useRef, useEffect, useCallback } from 'react'

interface UseInlineEditorBehaviorOptions {
  /** The save function to call. Should handle its own value parsing, change detection, and saving state. */
  onSave: () => void
  /** Called when user cancels editing (Escape key) */
  onCancel: () => void
  /** Ref to the container element for click-outside detection */
  containerRef: React.RefObject<HTMLElement | null>
  /**
   * Whether blur should check if focus moved within the container before saving.
   * Use this for multi-element editors (e.g., input + button).
   * Default: false
   */
  checkFocusWithinContainer?: boolean
  /**
   * Delay in ms before blur triggers save. Allows click events to fire first.
   * Default: 100
   */
  blurDelay?: number
}

interface UseInlineEditorBehaviorReturn {
  /** Keyboard handler for Enter (save) and Escape (cancel) */
  handleKeyDown: (e: React.KeyboardEvent) => void
  /** Blur handler that triggers save after a delay */
  handleBlur: (e: React.FocusEvent) => void
  /** Ref that tracks whether save was already triggered (e.g., by click outside). Check this in blur to avoid double-saves. */
  saveTriggeredRef: React.MutableRefObject<boolean>
}

/**
 * Shared behavior hook for simple inline editors (Price, Text, Date, etc.).
 *
 * Handles:
 * - Enter key → save, Escape key → cancel
 * - Click outside container → save
 * - Blur → save (with configurable delay and double-save prevention)
 *
 * The component is responsible for its own saving state (isSaving/setIsSaving).
 * Pass the component's handleSave as onSave - it should check isSaving internally.
 */
export function useInlineEditorBehavior({
  onSave,
  onCancel,
  containerRef,
  checkFocusWithinContainer = false,
  blurDelay = 100
}: UseInlineEditorBehaviorOptions): UseInlineEditorBehaviorReturn {
  const saveTriggeredRef = useRef(false)

  // Use a ref to always have the latest onSave without re-registering effects
  const onSaveRef = useRef(onSave)
  useEffect(() => { onSaveRef.current = onSave }, [onSave])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        onSaveRef.current()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        onCancel()
      }
    },
    [onCancel]
  )

  // Click outside → save
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        saveTriggeredRef.current = true
        onSaveRef.current()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [containerRef])

  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      if (checkFocusWithinContainer) {
        const container = containerRef.current
        const relatedTarget = e.relatedTarget as Node | null
        if (container && relatedTarget && container.contains(relatedTarget)) {
          // Focus moved within container, don't save yet
          return
        }
      }

      setTimeout(() => {
        if (!saveTriggeredRef.current) {
          onSaveRef.current()
        }
      }, blurDelay)
    },
    [checkFocusWithinContainer, containerRef, blurDelay]
  )

  return {
    handleKeyDown,
    handleBlur,
    saveTriggeredRef
  }
}

